import React, { useState,useContext } from 'react';
import { UserContext } from '../../../lib/usercontext';
import axios from 'axios';
import { getHeaders } from '../../../lib/requests';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import GeneralButton from '../button_general';
import ClientModal from '../modals/client_modal';
import { handlePdfFile, extractErrorMessage } from '../../../lib/utils';

function DeliveryFilterComponent({ onFilterChange }) {
  const { user } = useContext(UserContext);
  const role = user.userGroups ? user.userGroups[0] : 'Customer';
  const isAdmin = role === 'Admin';

  const navigate = useNavigate();
  const location = useLocation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [routeName, setRouteName] = useState('');

  const handleFilterApply = () => {
    const params = new URLSearchParams(location.search);

    if (startDate) {
      params.set('scheduled_delivery_date_start', startDate.toISOString().split('T')[0]);
    } else {
      params.delete('scheduled_delivery_date_start');
    }

    if (endDate) {
      params.set('scheduled_delivery_date_end', endDate.toISOString().split('T')[0]);
    } else {
      params.delete('scheduled_delivery_date_end');
    }

    if (deliveryStatus) {
      params.set('delivery_status', deliveryStatus);
    } else {
      params.delete('delivery_status');
    }

    if (selectedClient) {
      params.set('client_id', selectedClient.client_id);
    } else {
      params.delete('client_id');
    }

    if (routeName) {
      params.set('route_name', routeName);
    } else {
      params.delete('route_name');
    }

    navigate({ search: params.toString() });
    onFilterChange(selectedClient);
  };

  const handleFilterClear = () => {
    setStartDate(null);
    setEndDate(null);
    setDeliveryStatus('');
    setSelectedClient(null);
    setRouteName('');

    const params = new URLSearchParams(location.search);
    params.delete('scheduled_delivery_date_start');
    params.delete('scheduled_delivery_date_end');
    params.delete('delivery_status');
    params.delete('client_id');
    params.delete('route_name');

    navigate({ search: params.toString() });
    onFilterChange(null);
  };

  const handleProductionSheetPrint = async () => {
      try {
          const apiUrl = process.env.REACT_APP_API_URL;
          const apiToken = localStorage.getItem('accessToken');
          
          const response = await axios.post(
              `${apiUrl}/reports/production_items_types/pdf`,
              {
                  start_date: startDate?.toISOString().split('T')[0],
                  end_date: endDate?.toISOString().split('T')[0],
                  delivery_status: deliveryStatus || "scheduled",
              },
              {
                  headers: getHeaders(apiToken),
                  responseType: 'blob', // Ensure response is treated as binary
              }
          );
          
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'production_sheet_' + startDate?.toISOString().split('T')[0] + '_' + endDate?.toISOString().split('T')[0] + '.pdf';
          
          if (contentDisposition) {
              const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
              if (fileNameMatch && fileNameMatch[1]) {
                  fileName = fileNameMatch[1];
              }
          }
          
          const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
          handlePdfFile(pdfBlob, fileName, 'open');
      } catch (error) {
          console.error('Error generating PDF:', error);
          if (
            (error.response && [401, 403].includes(error.response.status)) ||
            !localStorage.getItem('accessToken')
          ) {
            navigate('/login?sessionExpired=true', { replace: true });
            return;
          }
          const message = await extractErrorMessage(error);
          alert(`Failed to generate PDF: ${message}`);
      }
  };

  return (
    <div className="filter-component flex flex-wrap items-end space-x-4 mb-0 ml-2">
      <div className="flex flex-col mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white" htmlFor="start_date">Delivery Start Date</label>
        <DatePicker
          id="start_date"
          selected={startDate}
          onChange={date => setStartDate(date)}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          dateFormat="yyyy-MM-dd"
          className="p-2 border border-gray-300 rounded bg-gray-50 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
        />
      </div>
      <div className="flex flex-col mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white" htmlFor="end_date">Delivery End Date</label>
        <DatePicker
          id="end_date"
          selected={endDate}
          onChange={date => setEndDate(date)}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          dateFormat="yyyy-MM-dd"
          className="p-2 border border-gray-300 rounded bg-gray-50 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
        />
      </div>
      <div className="flex flex-col mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white" htmlFor="delivery_status">Delivery Status</label>
        <select
          id="delivery_status"
          value={deliveryStatus}
          onChange={(e) => setDeliveryStatus(e.target.value)}
          className="p-2 border border-gray-300 rounded bg-gray-50 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 min-w-[150px]"
        >
          <option value="">Select</option>
          <option value="scheduled">Scheduled</option>
          <option value="delivered">Delivered</option>
          <option value="canceled">Canceled</option>
        </select>
      </div>
      <div className="flex flex-col mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white" htmlFor="route_name">Route Name</label>
        <input
          id="route_name"
          value={routeName}
          onChange={(e) => setRouteName(e.target.value)}
          className="p-2 border border-gray-300 rounded bg-gray-50 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 min-w-[150px]"
          placeholder="Enter route name"
        />
      </div>
      <div className="flex flex-col">
        {isAdmin && (
          <ClientModal
            isOpen={isClientModalOpen}
            setIsOpen={setIsClientModalOpen}
            selectClient={setSelectedClient}
            value={selectedClient}
            isSelectable={true}
            useButton={false}
          />
        )}        
      </div>
      <div className="flex flex-col mb-4">
        <GeneralButton type="button" buttonType="primary" onClick={() => handleFilterApply()}>
          Apply Filters
        </GeneralButton>
      </div>
      <div className="flex flex-col mb-4">
        <GeneralButton type="button" buttonType="secondary" onClick={() => handleFilterClear()}>
          Clear Filters
        </GeneralButton>
      </div>
      <div className="flex flex-col mb-4">
        {isAdmin && (<GeneralButton
          type="button"
          buttonType="secondary"
          onClick={handleProductionSheetPrint}
          disabled={!startDate || !endDate}
        >
          Print Production Sheet
        </GeneralButton>) }
      </div>
    </div>
  );
}

export default DeliveryFilterComponent;
